import axios from 'axios';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, Eye, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Store } from '../../Store';
import PageHeader from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import ConfirmModal from '../../components/ConfirmModal';
import Pagination from '../../components/Pagination/Pagination';
import { getError } from '../../utils';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        vendedores: action.payload.vendedores,
        loading: false,
        itemQuantity: action.payload.totalAmount,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'DELETE_REQUEST':
      return { ...state, loadingDelete: true, successDelete: false };
    case 'DELETE_SUCCESS':
      return { ...state, loadingDelete: false, successDelete: true };
    case 'DELETE_FAIL':
      return { ...state, loadingDelete: false, successDelete: false };
    case 'DELETE_RESET':
      return { ...state, loadingDelete: false, successDelete: false };
    default:
      return state;
  }
};

export default function ListaVendedores() {
  const { t } = useTranslation();
  const [{ loading, error, vendedores, successDelete, itemQuantity }, dispatch] = useReducer(
    reducer,
    { vendedores: [], loading: true, error: '', itemQuantity: 0 }
  );

  const { state } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [showModal, setShowModal] = useState(false);
  const [vendedorToDelete, setVendedorToDelete] = useState(null);

  const onPageChange = async pageNumber => {
    setCurrentPage(pageNumber);
    await fetchData((pageNumber - 1) * itemsPerPage);
  };

  const fetchData = async skip => {
    try {
      dispatch({ type: 'FETCH_REQUEST' });
      const url = userInfo.isSuperAdmin
        ? `/api/vendedores/${skip}/${itemsPerPage}`
        : `/api/vendedores/filtroDescripcion/${userInfo.descripcion}/${skip}/${itemsPerPage}`;
      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      dispatch({ type: 'FETCH_SUCCESS', payload: data });
    } catch (err) {
      dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
    }
  };

  useEffect(() => {
    if (successDelete) {
      dispatch({ type: 'DELETE_RESET' });
    } else {
      fetchData(0);
    }
  }, [successDelete]);

  const handleDeleteClick = vendedor => {
    setVendedorToDelete(vendedor);
    setShowModal(true);
  };

  const deleteHandler = async () => {
    try {
      dispatch({ type: 'DELETE_REQUEST' });
      await axios.delete(`/api/vendedores/${vendedorToDelete._id}`, {
        headers: { authorization: `Bearer ${userInfo.token}` },
      });
      dispatch({ type: 'DELETE_SUCCESS' });
      toast.success(t('seller.sellerDeletedSuccess'));
      setShowModal(false);
      setVendedorToDelete(null);
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'DELETE_FAIL' });
    }
  };

  return (
    <div className='space-y-6'>
      <ConfirmModal
        show={showModal}
        title={t('common.delete')}
        message={t('common.areYouSureDelete')}
        onConfirm={deleteHandler}
        onCancel={() => {
          setShowModal(false);
          setVendedorToDelete(null);
        }}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive
      />

      <PageHeader
        title={t('seller.sellerList')}
        subtitle='Vendedores registrados en el sistema'
        actions={
          <Button onClick={() => navigate('/AdminScreen/nuevoVendedor')}>
            <Plus className='h-4 w-4' />
            {t('seller.createSeller')}
          </Button>
        }
      />

      <Card>
        <CardContent className='p-0'>
          {loading ? (
            <div className='divide-y divide-border'>
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className='flex items-center gap-4 px-6 py-4'>
                  <div className='h-3 w-1/4 animate-pulse rounded bg-muted' />
                  <div className='h-3 w-1/4 animate-pulse rounded bg-muted' />
                  <div className='h-3 w-1/6 animate-pulse rounded bg-muted' />
                </div>
              ))}
            </div>
          ) : error ? (
            <p className='px-6 py-5 text-sm text-destructive'>{error}</p>
          ) : !vendedores || vendedores.length === 0 ? (
            <div className='px-6 py-12 text-center text-sm text-muted-foreground'>
              <p>No hay vendedores todavía.</p>
              <div className='mt-3'>
                <Button size='sm' onClick={() => navigate('/AdminScreen/nuevoVendedor')}>
                  Agregar vendedor
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common.name')}</TableHead>
                  <TableHead>{t('seller.description')}</TableHead>
                  <TableHead>{t('common.category')}</TableHead>
                  <TableHead>{t('common.phone')}</TableHead>
                  <TableHead>{t('common.email')}</TableHead>
                  <TableHead className='w-[120px]' />
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendedores.map(vendedor => (
                  <TableRow key={vendedor._id}>
                    <TableCell className='font-medium'>{vendedor.nombre || '—'}</TableCell>
                    <TableCell className='text-muted-foreground'>
                      {vendedor.descripcion || '—'}
                    </TableCell>
                    <TableCell className='text-muted-foreground'>
                      {vendedor.categoria || '—'}
                    </TableCell>
                    <TableCell className='text-muted-foreground'>
                      {vendedor.telefono || '—'}
                    </TableCell>
                    <TableCell className='text-muted-foreground'>{vendedor.email || '—'}</TableCell>
                    <TableCell>
                      <div className='flex items-center justify-end gap-1'>
                        <Button
                          variant='ghost'
                          size='icon-sm'
                          title={t('common.viewDetails')}
                          onClick={() => navigate(`/AdminScreen/vendedor/${vendedor._id}`)}
                        >
                          <Eye className='h-4 w-4' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='icon-sm'
                          title={t('common.edit')}
                          onClick={() => navigate(`/AdminScreen/editVendedores/${vendedor._id}`)}
                        >
                          <Pencil className='h-4 w-4' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='icon-sm'
                          title={t('common.delete')}
                          className='text-destructive hover:text-destructive'
                          onClick={() => handleDeleteClick(vendedor)}
                        >
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {itemQuantity > 0 && (
        <Pagination
          className='pagination-bar'
          totalCount={itemQuantity}
          onPageChange={onPageChange}
          currentPage={currentPage}
          pageSize={itemsPerPage}
        />
      )}
    </div>
  );
}
